import { Button } from "$components/button/button";
import { Page } from "$components/page";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { triggerBrowserCsvDownload } from "$routes/_lib/bulk-operations/browser-utils";
import { LocationSelect } from "$routes/inventory/_lib/location-select";
import { Box, Typography } from "@material-ui/core";
import type { StockTakeBulkRowInput } from "elephant-web/gql";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { linkToHistoricStockTakeOverview } from "../links";
import {
  type StockTakeBulkUploadMutation,
  useStockTakeBulkDownloadLazyQuery,
  useStockTakeBulkUploadMutation,
  useValidateStockTakeBulkUploadLazyQuery,
  type ValidateStockTakeBulkUploadQuery,
} from "./_lib/__generated__/bulk-stocktake-operations.gql";
import { StockTakeBulkPreviewTable } from "./_lib/stock-take-bulk-preview-table";

type ValidationResult = NonNullable<
  ValidateStockTakeBulkUploadQuery["validateStockTakeBulkUpload"]
>;
type UploadResult = StockTakeBulkUploadMutation["stockTakeBulkUpload"];

const Detail: FC<{ label: React.ReactNode; value: number }> = ({
  label,
  value,
}) => (
  <Box marginRight={4}>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

export const BulkStocktakePage: FC = () => {
  const { facilityId } = useUser();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const [downloadExport] = useStockTakeBulkDownloadLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [validateUpload] = useValidateStockTakeBulkUploadLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [commitUpload] = useStockTakeBulkUploadMutation();

  const [locationId, setLocationId] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);

  // Switching location invalidates any preview/result — they were computed for
  // the previous location, and committing them would write against the wrong one.
  function onLocationChange(nextLocationId: string | null) {
    setLocationId(nextLocationId ?? "");
    setValidation(null);
    setResult(null);
  }

  async function onDownloadExport() {
    setDownloading(true);
    try {
      const { data } = await downloadExport({
        variables: { facilityId, locationId },
      });
      if (!data) {
        throw new Error("No export returned");
      }
      triggerBrowserCsvDownload(
        data.stockTakeBulkDownload.csv,
        "bulk-stocktake.csv",
      );
    } catch (_e) {
      toast.error(
        formatMessage({
          id: "inventory.bulkStocktake.download.error",
          defaultMessage: "Could not export the stock list. Please try again.",
        }),
      );
    } finally {
      setDownloading(false);
    }
  }

  async function validateCsv(csv: string) {
    setValidating(true);
    setResult(null);
    try {
      const { data } = await validateUpload({
        variables: { input: { facilityId, locationId, csv } },
      });
      const validated = data?.validateStockTakeBulkUpload;
      if (!validated) {
        throw new Error("Validation returned no result");
      }
      setValidation(validated);
    } catch (_e) {
      toast.error(
        formatMessage({
          id: "inventory.bulkStocktake.validate.error",
          defaultMessage: "Could not validate the file. Please try again.",
        }),
      );
    } finally {
      setValidating(false);
    }
  }

  function onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => validateCsv(e.target?.result as string);
    reader.readAsText(file);
  }

  const rows = validation?.rows ?? [];
  const hasChanges = (validation?.totalToUpdate ?? 0) > 0;
  // Hoisted to a const so the && narrowing holds inside the onClick closure.
  const finalisedStockTakeId = result?.stockTakeId ?? null;

  async function onCommit() {
    setCommitting(true);
    try {
      const changedRows: StockTakeBulkRowInput[] = rows
        .filter(
          (row) =>
            row.willUpdate &&
            !row.hasError &&
            row.lotId != null &&
            row.actualCount != null,
        )
        .map((row) => ({
          lotId: row.lotId as string,
          actualCount: row.actualCount as number,
        }));

      const { data } = await commitUpload({
        variables: { input: { facilityId, locationId, rows: changedRows } },
      });
      if (data) {
        setResult(data.stockTakeBulkUpload);
        if (data.stockTakeBulkUpload.totalErrors === 0) {
          toast.success(
            formatMessage({
              id: "inventory.bulkStocktake.commit.success",
              defaultMessage: "Stocktake applied successfully.",
            }),
          );
        }
      }
    } catch (_e) {
      toast.error(
        formatMessage({
          id: "inventory.bulkStocktake.commit.error",
          defaultMessage: "Could not apply the stocktake. Please try again.",
        }),
      );
    } finally {
      setCommitting(false);
    }
  }

  return (
    <Page data-dense>
      <Typography variant="h4" gutterBottom>
        <FormattedMessage
          id="inventory.bulkStocktake.heading"
          defaultMessage="Bulk stocktake"
        />
      </Typography>

      <Box marginBottom={2}>
        <ol style={{ marginTop: 0, paddingLeft: "1.25rem" }}>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkStocktake.steps.location"
                defaultMessage="Choose the location you're counting."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkStocktake.steps.export"
                defaultMessage="Click <b>Export current stock</b> to download a CSV of every lot at that location, with the system quantity and a blank <b>actualCount</b> column."
                values={{ b: (chunks) => <strong>{chunks}</strong> }}
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkStocktake.steps.count"
                defaultMessage="Open it in Excel or Google Sheets and fill in the actual count for each lot you've counted. Leave a row blank to leave that lot unchanged. Enter 0 if you counted none."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkStocktake.steps.upload"
                defaultMessage="Save it as a CSV and upload it below to preview the discrepancies."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.bulkStocktake.steps.apply"
                defaultMessage="Review the preview, then click <b>Apply stocktake</b>. Each counted lot is set to its actual count."
                values={{ b: (chunks) => <strong>{chunks}</strong> }}
              />
            </Typography>
          </li>
        </ol>
      </Box>

      <Box maxWidth="360px" marginBottom={2}>
        <LocationSelect
          id="bulk-stocktake-location"
          value={locationId || null}
          onChange={onLocationChange}
          label={formatMessage({
            id: "inventory.bulkStocktake.location.label",
            defaultMessage: "Location",
          })}
        />
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={2}
        flexWrap="wrap"
      >
        <Button
          loading={downloading}
          disabled={!locationId}
          onClick={onDownloadExport}
          data-testid="bulk-stocktake-export"
        >
          <FormattedMessage
            id="inventory.bulkStocktake.export"
            defaultMessage="Export current stock"
          />
        </Button>

        <Box maxWidth="360px">
          <Typography variant="body2" gutterBottom>
            <FormattedMessage
              id="inventory.bulkStocktake.upload.label"
              defaultMessage="Upload your completed CSV:"
            />
          </Typography>
          <input
            data-testid="bulk-stocktake-file-input"
            type="file"
            accept=".csv"
            disabled={!locationId}
            onChange={onFileChange}
          />
        </Box>
      </Box>

      {validating && (
        <Typography variant="body2">
          <FormattedMessage
            id="inventory.bulkStocktake.validating"
            defaultMessage="Validating file…"
          />
        </Typography>
      )}

      {result && (
        <Box
          marginBottom={3}
          data-testid="bulk-stocktake-result"
          padding={2}
          bgcolor="grey.100"
          borderRadius={4}
        >
          <Typography variant="h6" gutterBottom>
            <FormattedMessage
              id="inventory.bulkStocktake.result.heading"
              defaultMessage="Stocktake complete"
            />
          </Typography>
          <Box display="flex" flexWrap="wrap" marginBottom={1}>
            <Detail
              label={
                <FormattedMessage
                  id="inventory.bulkStocktake.result.totalSent"
                  defaultMessage="Rows sent"
                />
              }
              value={result.totalSent}
            />
            <Detail
              label={
                <FormattedMessage
                  id="inventory.bulkStocktake.result.totalUpdated"
                  defaultMessage="Lots updated"
                />
              }
              value={result.totalUpdated}
            />
            <Detail
              label={
                <FormattedMessage
                  id="inventory.bulkStocktake.result.totalErrors"
                  defaultMessage="Errors"
                />
              }
              value={result.totalErrors}
            />
          </Box>
          {result.errors.length > 0 && (
            <ul>
              {result.errors.map((error) => (
                <li key={error.id}>
                  <Typography variant="body2" color="error">
                    {error.errorMessage}
                  </Typography>
                </li>
              ))}
            </ul>
          )}
          {finalisedStockTakeId && (
            <Button
              color="primary"
              onClick={() =>
                navigate(linkToHistoricStockTakeOverview(finalisedStockTakeId))
              }
              data-testid="bulk-stocktake-view-report"
            >
              <FormattedMessage
                id="inventory.bulkStocktake.result.viewReport"
                defaultMessage="View discrepancy report"
              />
            </Button>
          )}
        </Box>
      )}

      {!result && rows.length > 0 && (
        <>
          {(validation?.totalModifiedSinceExport ?? 0) > 0 && (
            <Box
              marginBottom={2}
              padding={1.5}
              bgcolor="warning.light"
              borderRadius={4}
              data-testid="bulk-stocktake-stale-warning"
            >
              <Typography variant="body2">
                <FormattedMessage
                  id="inventory.bulkStocktake.staleWarning"
                  defaultMessage="{count, plural, one {# lot has} other {# lots have}} been modified since this file was exported. Review before confirming."
                  values={{ count: validation?.totalModifiedSinceExport ?? 0 }}
                />
              </Typography>
            </Box>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginBottom={2}
          >
            <Box display="flex" flexWrap="wrap">
              <Detail
                label={
                  <FormattedMessage
                    id="inventory.bulkStocktake.summary.totalSent"
                    defaultMessage="Rows"
                  />
                }
                value={validation?.totalSent ?? 0}
              />
              <Detail
                label={
                  <FormattedMessage
                    id="inventory.bulkStocktake.summary.toUpdate"
                    defaultMessage="To update"
                  />
                }
                value={validation?.totalToUpdate ?? 0}
              />
              <Detail
                label={
                  <FormattedMessage
                    id="inventory.bulkStocktake.summary.unchanged"
                    defaultMessage="Unchanged"
                  />
                }
                value={validation?.totalUnchanged ?? 0}
              />
              <Detail
                label={
                  <FormattedMessage
                    id="inventory.bulkStocktake.summary.errors"
                    defaultMessage="Errors"
                  />
                }
                value={validation?.totalErrors ?? 0}
              />
            </Box>
            <Button
              color="primary"
              variant="contained"
              loading={committing}
              disabled={!hasChanges}
              onClick={onCommit}
              data-testid="bulk-stocktake-commit"
            >
              <FormattedMessage
                id="inventory.bulkStocktake.commit"
                defaultMessage="Apply stocktake"
              />
            </Button>
          </Box>

          {!hasChanges && (
            <Box marginBottom={2}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="inventory.bulkStocktake.commit.noChanges"
                  defaultMessage="No counted lot differs from the system quantity, so there's nothing to apply."
                />
              </Typography>
            </Box>
          )}

          <StockTakeBulkPreviewTable rows={rows} />
        </>
      )}
    </Page>
  );
};
